import { useEffect, useState, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { 
  initializeAbly,
  addConnectionListener,
  getConnectionState,
  setQueryClient,
  subscribeToInternalChat,
  unsubscribeFromInternalChat
} from '@/lib/ably';
import { queryKeys } from '@/lib/query-keys';
import { useAuthStore } from '@/stores/authStore';
import { usePingStore } from '@/stores/ping-store';
import type { InternalChat } from '@/types';

// Track which internal chats are currently subscribed for the chat list
const activeInternalChatListSubscriptions = new Set<string>();

// Subscribe to multiple internal chats for chat list updates using centralized subscription
const subscribeToInternalChatsForList = (chatIds: string[]) => {
  // Unsubscribe from chats that are no longer in the list
  const chatsToUnsubscribe = Array.from(activeInternalChatListSubscriptions).filter(
    chatId => !chatIds.includes(chatId)
  );
  
  chatsToUnsubscribe.forEach(chatId => {
    unsubscribeFromInternalChat(chatId, 'chatlist');
    activeInternalChatListSubscriptions.delete(chatId);
  });

  // Subscribe to new chats
  chatIds.forEach(chatId => {
    if (!activeInternalChatListSubscriptions.has(chatId)) {
      try {
        // Use centralized subscription with 'chatlist' context
        subscribeToInternalChat(chatId, 'chatlist');
        activeInternalChatListSubscriptions.add(chatId);
      } catch (error) {
        console.error(`Error subscribing to internal chat ${chatId} for list updates:`, error);
      }
    }
  });
};

// Unsubscribe from all internal chat list subscriptions
const unsubscribeFromAllInternalChatsForList = () => {
  activeInternalChatListSubscriptions.forEach(chatId => {
    unsubscribeFromInternalChat(chatId, 'chatlist');
  });
  activeInternalChatListSubscriptions.clear();
};

// Hook for managing real-time internal chat list updates
export const useRealtimeInternalChats = (chats: InternalChat[], isInactiveTab: boolean = false) => {
  const { isAuthenticated } = useAuthStore();
  const { selectedChatId, setTeamsUnreadCount, teamsUnreadCount } = usePingStore();
  const [connectionState, setConnectionState] = useState(getConnectionState());
  const queryClient = useQueryClient();
  
  // Extract chat IDs from the chats array
  const chatIds = useMemo(() => {
    return chats.map(chat => chat.id);
  }, [chats]);

  // Set the queryClient in Ably module on first use
  useEffect(() => {
    setQueryClient(queryClient);
  }, [queryClient]);

  // Monitor connection state
  useEffect(() => {
    const removeListener = addConnectionListener(setConnectionState);
    return () => {
      removeListener();
    };
  }, []);

  // Initialize Ably connection on mount
  useEffect(() => {
    if (isAuthenticated) {
      initializeAbly().catch(error => {
        console.error('Failed to initialize Ably:', error);
      });
    }
  }, [isAuthenticated]);

  // Subscribe to multiple internal chats when connection is ready and chats are available
  useEffect(() => {
    if (!isAuthenticated || !connectionState.isConnected || chatIds.length === 0) {
      return;
    }
    
    subscribeToInternalChatsForList(chatIds);

    // Cleanup on unmount or when chat IDs change
    return () => {
      unsubscribeFromAllInternalChatsForList();
    };
  }, [chatIds, isAuthenticated, connectionState.isConnected]);
  
  // Listen for query cache updates to update inactive tab count
  useEffect(() => {
    if (!isInactiveTab || chats.length === 0) return;
    
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      // When internal chat list is updated due to new messages, recalculate and update the count
      const queryKey = event?.query?.queryKey;
      if (queryKey?.[0] === 'ping' && 
          queryKey?.[1] === 'internalChats' &&
          event?.type === 'updated') {
        // Calculate new unread count from updated data
        const data = event.query.state.data as any;
        if (data?.results && Array.isArray(data.results)) {
          const unreadCount = data.results.filter((chat: InternalChat) => chat.unread_count > 0).length;
          // Update the count directly from the fresh data
          setTeamsUnreadCount(unreadCount);
        }
      }
    });
    
    return unsubscribe;
  }, [isInactiveTab, queryClient, setTeamsUnreadCount, chats.length]);

  // Force refresh of internal chat list data
  const refreshChatList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.ping.internalChats.all(),
    });
  }, [queryClient]);

  // Update unread count for currently viewing chat (set to 0)
  const markCurrentChatAsRead = useCallback(() => {
    if (selectedChatId) {
      // Optimistically update unread count in cache
      queryClient.setQueryData(
        queryKeys.ping.internalChats.all(),
        (oldData: any) => {
          if (!oldData?.results) return oldData;
          
          return {
            ...oldData,
            results: oldData.results.map((chat: InternalChat) => {
              if (chat.id === selectedChatId) {
                return {
                  ...chat,
                  unread_count: 0,
                  is_unread: false,
                };
              }
              return chat;
            })
          };
        }
      );
    }
  }, [queryClient, selectedChatId]);

  return {
    isConnected: connectionState.isConnected,
    connectionState,
    refreshChatList,
    markCurrentChatAsRead,
    activeSubscriptions: activeInternalChatListSubscriptions.size,
  };
};

